import { z } from 'zod';

/**
 * `pending-runner-shard-depth` — Playwright-grade `--shard k/N`.
 *
 * Shared between the CLI (`packages/cli/src/commands/run.ts`)
 * and the API (`packages/api/src/routes/runs.ts`) so the same
 * flow id always lands in the same shard regardless of where
 * the split happens.
 *
 * Design choices:
 *   • **Sort key is the flow `id`** (string). Stable, monotonic
 *     enough across renames, no Unicode collation surprises.
 *     `id` is used as a literal-string sort, NOT a numeric sort
 *     — Rynora flows are UUIDs in the cloud and arbitrary
 *     strings in local files.
 *   • **Round-robin distribution.** `shard k` (1-indexed) gets
 *     every `N`th flow starting at `k-1`. Simple, deterministic,
 *     no weighting in v1 — smart load-balancing is a v2 ask.
 *   • **Over-provisioned shards are fine.** If `N` > flow count,
 *     the tail shards get 0 flows and the runner exits 0.
 *   • **Mid-suite flow additions don't affect in-flight shards.**
 *     Each shard freezes its assignment list at start, then runs
 *     only those flows.
 */

/**
 * Zod-validated shard descriptor. The runtime form the API
 * persists into `suite_shard_runs`.
 */
export const ShardSpecSchema = z
  .object({
    shard_index: z.number().int().min(1),
    shard_total: z.number().int().min(1).max(1024),
  })
  .refine((s) => s.shard_index <= s.shard_total, {
    message: 'shard_index must be ≤ shard_total',
    path: ['shard_index'],
  });
export type ShardSpec = z.infer<typeof ShardSpecSchema>;

/**
 * Parse a `k/N` CLI argument into a validated `ShardSpec`.
 * Throws on malformed input with a friendly message.
 *
 *   parseShardSpec('2/4')   → { shard_index: 2, shard_total: 4 }
 *   parseShardSpec('5/4')   → throws (out of range)
 *   parseShardSpec('foo')   → throws (malformed)
 */
export function parseShardSpec(input: string): ShardSpec {
  const trimmed = input.trim();
  if (!/^\d+\/\d+$/.test(trimmed)) {
    throw new Error(
      `Invalid shard spec ${JSON.stringify(input)} — expected "k/N" (e.g. "2/4")`,
    );
  }
  const [k, n] = trimmed.split('/').map((p) => Number.parseInt(p, 10));
  return ShardSpecSchema.parse({ shard_index: k, shard_total: n });
}

/**
 * `formatShardLabel({ shard_index: 2, shard_total: 4 })` →
 * `"shard 2 of 4"`. Used in JUnit `<testsuite name="…">` and
 * the CLI summary line so a CI viewer can tell shards apart.
 */
export function formatShardLabel(spec: ShardSpec): string {
  return `shard ${spec.shard_index} of ${spec.shard_total}`;
}

/**
 * Deterministically partition a list of flows into shards.
 *
 * Generic over flow shape — the CLI passes its loose
 * `FlowFile` rows, the API passes server `Flow` rows, and both
 * get back a typed subset.
 *
 * Sort + filter:
 *   1. Sort by the string form of `flow[idKey]` (default `id`).
 *   2. Keep every `N`th flow starting at `k - 1`.
 *
 * Stable across calls: same input → same output.
 */
export interface AssignOptions<F> {
  /** Key whose string value is used as the sort key. */
  idKey?: keyof F;
}

export function assignFlowsToShard<F extends Record<string, unknown>>(
  flows: F[],
  spec: ShardSpec,
  options: AssignOptions<F> = {},
): F[] {
  if (flows.length === 0) return [];
  ShardSpecSchema.parse(spec);
  const idKey = (options.idKey ?? 'id') as keyof F;
  const sorted = [...flows].sort((a, b) => {
    const aKey = String(a[idKey] ?? '');
    const bKey = String(b[idKey] ?? '');
    return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
  });
  const out: F[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i % spec.shard_total === spec.shard_index - 1) out.push(sorted[i]);
  }
  return out;
}

/**
 * The wire schema for the new `suite_shard_runs` table — also
 * the shape `/api/runs/suite` and `/api/runs/suite/shards/:id`
 * round-trip in JSON.
 */
export const ShardStatusSchema = z.enum(['queued', 'running', 'passed', 'failed', 'cancelled']);
export type ShardStatus = z.infer<typeof ShardStatusSchema>;

export const SuiteShardRunSchema = z.object({
  id: z.string().uuid(),
  suite_run_id: z.string().uuid(),
  suite_name: z.string().nullable(),
  shard_index: z.number().int().min(1),
  shard_total: z.number().int().min(1),
  status: ShardStatusSchema,
  flow_ids_assigned: z.array(z.string()),
  flow_count: z.number().int().nonnegative(),
  passed_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  duration_ms: z.number().int().nonnegative(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  created_at: z.string(),
});
export type SuiteShardRun = z.infer<typeof SuiteShardRunSchema>;

export const ListSuiteShardsResponseSchema = z.object({
  suite_run_id: z.string().uuid(),
  shards: z.array(SuiteShardRunSchema),
  aggregate: z.object({
    shard_count: z.number().int().nonnegative(),
    shard_total_expected: z.number().int().nonnegative(),
    flow_count: z.number().int().nonnegative(),
    passed_count: z.number().int().nonnegative(),
    failed_count: z.number().int().nonnegative(),
    status: ShardStatusSchema.or(z.literal('partial')),
  }),
});
export type ListSuiteShardsResponse = z.infer<typeof ListSuiteShardsResponseSchema>;
